import { salesIndigoOutlineBtnClass } from "@/components/common/surfaceClasses";
import type { Dispatch, SetStateAction } from "react";
import type { TrainingAnswer } from "../../../lib/training/types";
import type { GuidanceSnapshot } from "./trainingPanelGuidance";
import { TrainingAskProposalForm } from "./TrainingAskProposalForm";
import { TrainingAskThumbsDownForm } from "./TrainingAskThumbsDownForm";

type ThumbsDownReason =
  | ""
  | "wrong_answer"
  | "missing_detail"
  | "outdated_policy"
  | "slang_uncommon_phrasing"
  | "wrong_contacts";

type MessageTone = { tone: "ok" | "err"; text: string } | null;

type TrainingAskFeedbackActionsProps = {
  result: TrainingAnswer;
  answerMode: "kb" | "contacts" | "location";
  scopedOrganizationId: string;
  resultQuery: string;
  lastQuery: string;
  query: string;
  salespersonName?: string;
  salespersonFirstName?: string;
  appVersion: string;
  webhookUrl?: string;
  locationId?: string;
  locationName?: string;
  askThreadId: string | null;
  askThreadTurnsCount: number;
  nextAskKind: "new" | "followup";
  canEscalateKbThread: boolean;
  activeGuidance: GuidanceSnapshot | null;
  kbTestingModeEnabled: boolean;
  proposeOpen: boolean;
  setProposeOpen: Dispatch<SetStateAction<boolean>>;
  proposeTitle: string;
  setProposeTitle: Dispatch<SetStateAction<string>>;
  proposeBody: string;
  setProposeBody: Dispatch<SetStateAction<string>>;
  proposeSubmitting: boolean;
  setProposeSubmitting: Dispatch<SetStateAction<boolean>>;
  proposeMessage: MessageTone;
  setProposeMessage: Dispatch<SetStateAction<MessageTone>>;
  thumbsDownOpen: boolean;
  setThumbsDownOpen: Dispatch<SetStateAction<boolean>>;
  thumbsDownBusy: boolean;
  setThumbsDownBusy: Dispatch<SetStateAction<boolean>>;
  thumbsDownReason: ThumbsDownReason;
  setThumbsDownReason: Dispatch<SetStateAction<ThumbsDownReason>>;
  thumbsDownNotes: string;
  setThumbsDownNotes: Dispatch<SetStateAction<string>>;
  thumbsDownExpectedAnswer: string;
  setThumbsDownExpectedAnswer: Dispatch<SetStateAction<string>>;
  thumbsDownImpact: string;
  setThumbsDownImpact: Dispatch<SetStateAction<string>>;
  thumbsDownMessage: MessageTone;
  setThumbsDownMessage: Dispatch<SetStateAction<MessageTone>>;
};

export function TrainingAskFeedbackActions(props: TrainingAskFeedbackActionsProps) {
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700 dark:hover:bg-zinc-700"
          onClick={() => {
            props.setThumbsDownOpen((v) => !v);
            props.setThumbsDownMessage(null);
          }}
        >
          Thumbs down
        </button>

        {props.answerMode === "kb" ? (
          <button
            type="button"
            className={`px-3 py-1.5 rounded-xl text-sm font-semibold ${salesIndigoOutlineBtnClass} disabled:cursor-not-allowed`}
            disabled={!props.scopedOrganizationId}
            title={
              props.scopedOrganizationId
                ? "Submit proposed KB article text for admin review"
                : "Pick a workspace organization to submit KB proposals"
            }
            onClick={() => {
              props.setProposeMessage(null);
              const q = (props.resultQuery || props.lastQuery || props.query).trim();
              props.setProposeTitle(q.length > 120 ? `${q.slice(0, 117)}...` : q || "KB article proposal");
              props.setProposeBody("");
              props.setProposeOpen((o) => !o);
            }}
          >
            {props.proposeOpen ? "Hide proposal" : "Propose KB article"}
          </button>
        ) : null}
      </div>

      <TrainingAskThumbsDownForm
        open={props.thumbsDownOpen}
        setOpen={props.setThumbsDownOpen}
        busy={props.thumbsDownBusy}
        setBusy={props.setThumbsDownBusy}
        reason={props.thumbsDownReason}
        setReason={props.setThumbsDownReason}
        notes={props.thumbsDownNotes}
        setNotes={props.setThumbsDownNotes}
        expectedAnswer={props.thumbsDownExpectedAnswer}
        setExpectedAnswer={props.setThumbsDownExpectedAnswer}
        impact={props.thumbsDownImpact}
        setImpact={props.setThumbsDownImpact}
        message={props.thumbsDownMessage}
        setMessage={props.setThumbsDownMessage}
        kbTestingModeEnabled={props.kbTestingModeEnabled}
        result={props.result}
        appVersion={props.appVersion}
        resultQuery={props.resultQuery}
        lastQuery={props.lastQuery}
        query={props.query}
        askThreadId={props.askThreadId}
        askThreadTurnsCount={props.askThreadTurnsCount}
        nextAskKind={props.nextAskKind}
        locationId={props.locationId}
        locationName={props.locationName}
        salespersonFirstName={props.salespersonFirstName}
        salespersonName={props.salespersonName}
        scopedOrganizationId={props.scopedOrganizationId}
        canEscalateKbThread={props.canEscalateKbThread}
        activeGuidance={props.activeGuidance}
        webhookUrl={props.webhookUrl}
      />

      <TrainingAskProposalForm
        answerMode={props.answerMode}
        proposeOpen={props.proposeOpen}
        scopedOrganizationId={props.scopedOrganizationId}
        proposeTitle={props.proposeTitle}
        setProposeTitle={props.setProposeTitle}
        proposeBody={props.proposeBody}
        setProposeBody={props.setProposeBody}
        proposeSubmitting={props.proposeSubmitting}
        setProposeSubmitting={props.setProposeSubmitting}
        proposeMessage={props.proposeMessage}
        setProposeMessage={props.setProposeMessage}
        result={props.result}
        resultQuery={props.resultQuery}
        lastQuery={props.lastQuery}
        query={props.query}
        salespersonName={props.salespersonName}
        salespersonFirstName={props.salespersonFirstName}
      />
    </>
  );
}
